<template>
    <div
        ref="container"
        :style="gridStyle">
        <slot
            v-if="!hideLeading"
            name="leading" />
        <div
            v-if="!hideLeading && !hideTrailing"
            class="splitter"
            role="separator"
            :class="{ hover: isHovering || isDragging }">
            <div
                class="splitter-hit"
                :style="splitterHitStyle"
                @mouseenter="isHovering = true"
                @mouseleave="isHovering = false"
                @mousedown.left.prevent="startDragging" />
            <div
                v-if="showHandle"
                class="splitter-handle"
                :class="{ dragging: isDragging }"
                :style="splitterHandleStyle"
                @mouseenter="isHovering = true"
                @mouseleave="isHovering = false"
                @mousedown.left.prevent="startDragging">
                <pendo-icon
                    :type="vertical ? 'draggable-horizontal' : 'draggable-vertical'"
                    size="12" />
            </div>
        </div>
        <slot
            v-if="!hideTrailing"
            name="trailing" />
        <div
            v-if="isDragging"
            class="drag-overlay"
            :style="{ cursor: vertical ? 'row-resize' : 'col-resize' }" />
    </div>
</template>

<script>
import { useElementBounding, useEventListener } from '@vueuse/core';
import { useTemplateRef } from 'vue';
import { PendoIcon } from '@pendo/components';

export default {
    name: 'SplitPane',
    components: {
        PendoIcon,
    },
    props: {
        /**
         * Whether to hide the leading pane, showing only the trailing pane and no splitter
         */
        hideLeading: {
            type: Boolean,
            default: false,
        },
        /**
         * Whether to hide the trailing pane, showing only the leading pane and no splitter
         */
        hideTrailing: {
            type: Boolean,
            default: false,
        },
        /**
         * The initial size of the leading pane (a value between 0 and 1). If omitted,
         * the default is a 50/50 split.
         */
        initialSizeLeading: {
            type: Number,
            default: null,
        },
        /**
         * The smallest allowable size in pixels for the leading pane
         */
        minSizeLeading: {
            type: Number,
            default: 0,
        },
        /**
         * The smallest allowable size in pixels for the trailing pane
         */
        minSizeTrailing: {
            type: Number,
            default: 0,
        },
        /**
         * Whether to display the drag handle
         */
        showHandle: {
            type: Boolean,
            default: true,
        },
        /**
         * The width (for horizontal split) or height (for vertical split) of the splitter
         * in pixels
         */
        splitterSize: {
            type: Number,
            default: 1,
        },
        /**
         * The width (for horizontal split) or height (for vertical split) of the area that
         * activates the hover styling of the splitter.
         */
        splitterHitSize: {
            type: Number,
            default: 9,
        },
        /**
         * Whether to use a vertical split instead of the default horizontal split. For a
         * horizontal split the leading pane is on the left and trailing on the right, and
         * for a vertical split the leading pane is on top and trailing on the bottom.
         */
        vertical: {
            type: Boolean,
            default: false,
        },
    },
    setup () {
        const container = useTemplateRef('container');

        return useElementBounding(container);
    },
    data () {
        return {
            isDragging: false,
            isHovering: false,
            mouseMoveListener: null,
            position: null,
            useTransition: true,
        };
    },
    computed: {
        gridStyle () {
            return {
                'display': 'grid',
                'transition': this.useTransition ? '0.2s' : undefined,
                'grid-template-columns': this.vertical ? 'minmax(0, 1fr)' : this.gridTemplateVariable,
                'grid-template-rows': this.vertical ? this.gridTemplateVariable : 'minmax(0, 1fr)',
            };
        },
        gridTemplateVariable () {
            if (this.hideLeading || this.hideTrailing) {
                // Explicit zeroes are required for CSS transitions to work
                return '1fr 0 0';
            }

            const splitterSize = this.splitterSize || 1;
            const available = this.size - splitterSize;

            const minLeadingPos = (this.minSizeLeading / available).toFixed(2);
            const maxTrailingPos = (1 - this.minSizeTrailing / available).toFixed(2);
            const fallbackLeadingSize = this.initialSizeLeading !== null && this.initialSizeLeading > 0 && this.initialSizeLeading < 1
                ? this.initialSizeLeading
                : 0.5;

            const position =
                this.position === null || available < this.minSizeLeading + this.minSizeTrailing
                    ? fallbackLeadingSize
                    : clamp(this.position, minLeadingPos, maxTrailingPos);

            return `${position}fr ${this.splitterSize || 1}px ${1 - position}fr`;
        },
        size () {
            return this.vertical ? this.height : this.width;
        },
        splitterHandleStyle () {
            return {
                'cursor': this.vertical ? 'row-resize' : 'col-resize',
            };
        },
        splitterHitStyle () {
            return {
                'inset': this.vertical ? 'auto 0' : '0 auto',
                'min-width': this.vertical ? undefined : `${this.splitterHitSize}px`,
                'min-height': this.vertical ? `${this.splitterHitSize}px` : undefined,
                'cursor': this.vertical ? 'row-resize' : 'col-resize',
            };
        },
    },
    mounted () {
        useEventListener(document, 'mouseup', this.stopDragging);
    },
    methods: {
        startDragging () {
            this.useTransition = false;
            this.isDragging = true;
            window.document.body.style.cursor = this.vertical ? 'row-resize' : 'col-resize';
            this.mouseMoveListener = useEventListener(window, 'mousemove', (event) => {
                if (this.size < this.minSizeLeading + this.minSizeTrailing) {
                    return;
                }

                const mouse = this.vertical ? event.y - this.y : event.x - this.x;
                const position = clamp(mouse, this.minSizeLeading, this.size - this.minSizeTrailing);
                this.position = position / this.size;
            });
        },
        stopDragging () {
            this.mouseMoveListener?.();
            this.mouseMoveListener = null;
            this.useTransition = true;
            this.isDragging = false;
            window.document.body.style.cursor = '';
        },
    },
};

/**
 * Clamps number within the inclusive lower and upper bounds.
 */
function clamp (number, lower, upper) {
    if (number < lower) {
        return lower;
    }
    if (number > upper) {
        return upper;
    }

    return number;
}
</script>

<style scoped lang="less">
.splitter {
    display: flex;
    position: relative;
    align-items: center;
    justify-content: center;
    user-select: none;

    color: var(--palette-gray-50);
    background-color: var(--palette-gray-40);

    transition: background-color 0.2s ease-in-out,
                color 0.2s ease-in-out;

    &.hover {
        background-color: var(--palette-teal-70);
        color: var(--palette-teal-80)
    }
}

.splitter-hit {
    position: absolute;
}

.splitter-handle {
    position: absolute;
    padding: 4px 2px;

    background-color: var(--palette-gray-0);
    border-radius: 2px;
    box-shadow: 0 1px 1px 0 var(--semantic-drop-shadow-base);

    transition: background-color 0.1s ease-in-out;

    &.dragging {
        background-color: var(--palette-teal-10);
    }
}

// prevents iframes from registering the mouseup event when releasing resize
.drag-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
}
</style>
